<template>
  <div>
    <div
      ref="wrapper"
      :class="$style.wrapper"
      :style="{ maxHeight }"
      @scroll.passive="onScroll"
    >
      <div
        :class="$style.grid"
        :style="gridStyle"
      >
        <div
          v-for="item in visibleItems"
          :key="item.data.id"
          :data-index="item.index"
          :tabindex="item.index + 1"
          :class="getItemClasses(item.data.id)"
          @dblclick="openModal"
          @focus="navigate(item.data.id)"
          @keydown.arrow-up.prevent="onArrowUp(item.index)"
          @keydown.arrow-left.prevent="onArrowLeft(item.index)"
          @keydown.arrow-right.prevent="onArrowRight(item.index)"
          @keydown.arrow-down.prevent="onArrowDown(item.index)"
        >
          <div :class="$style.title">
            {{ item.data.date | dateTime }} / {{ item.data.caption }}
          </div>
          <div :class="$style.imageWrap">
            <img
              :class="$style.image"
              :src="getScreenshotUrl(item.data.path)"
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { dateTime } from '@/helpers/filters';
  import { getScreenshotUrl } from '@/helpers/methods';

  export default {
    filters: {
      dateTime,
    },
    props: {
      screenshots: {
        type: Array,
        required: true,
      },
      maxHeight: {
        type: String,
        default: '70vh',
      },
      rowHeight: {
        type: Number,
        default: 220,
      },
      bufferRows: {
        type: Number,
        default: 2,
      },
      virtualThreshold: {
        type: Number,
        default: 30,
      },
    },
    data() {
      return {
        columnsPerRow: 3,
        scrollTop: 0,
        viewportHeight: 0,
        resizeObserver: null,
      };
    },
    computed: {
      useVirtual() {
        return this.screenshots.length > this.virtualThreshold;
      },

      totalRows() {
        return Math.ceil(this.screenshots.length / this.columnsPerRow);
      },

      firstVisibleRow() {
        if (!this.useVirtual) return 0;

        return Math.max(0, Math.floor(this.scrollTop / this.rowHeight) - this.bufferRows);
      },

      lastVisibleRow() {
        if (!this.useVirtual) return this.totalRows;

        return Math.min(
          this.totalRows,
          Math.ceil((this.scrollTop + this.viewportHeight) / this.rowHeight) + this.bufferRows,
        );
      },

      startIndex() {
        return this.firstVisibleRow * this.columnsPerRow;
      },

      endIndex() {
        return Math.min(this.screenshots.length, this.lastVisibleRow * this.columnsPerRow);
      },

      visibleItems() {
        const out = [];

        for (let i = this.startIndex; i < this.endIndex; i++) {
          out.push({ data: this.screenshots[i], index: i });
        }

        return out;
      },

      gridStyle() {
        return {
          gridAutoRows: `${this.rowHeight}px`,
          paddingTop: `${this.useVirtual ? this.firstVisibleRow * this.rowHeight : 0}px`,
          paddingBottom: `${this.useVirtual ? (this.totalRows - this.lastVisibleRow) * this.rowHeight : 0}px`,
        };
      },
    },
    watch: {
      screenshots() {
        this.$nextTick(this.resetScroll);
      },
    },
    mounted() {
      this.$nextTick(() => {
        if (this.$refs.wrapper) {
          this.viewportHeight = this.$refs.wrapper.clientHeight;
        }
        this.setupResizeObserver();
      });
    },
    beforeDestroy() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
    },
    methods: {
      setupResizeObserver() {
        if (!window.ResizeObserver || !this.$refs.wrapper) return;

        this.resizeObserver = new ResizeObserver(([entry]) => {
          if (entry) {
            this.viewportHeight = entry.contentRect.height;
          }
        });

        this.resizeObserver.observe(this.$refs.wrapper);
      },

      onScroll(e) {
        this.scrollTop = e.target.scrollTop;
      },

      resetScroll() {
        if (!this.$refs.wrapper) return;

        this.$refs.wrapper.scrollTop = 0;
        this.scrollTop = 0;
      },
      getItemClasses(id) {
        const isActive = parseInt(this.$route.params.record, 10) === id;

        return [this.$style.itemTable, { [this.$style.itemTableActive]: isActive }];
      },
      getScreenshotUrl,
      openModal() {
        this.$router.replace({
          query: {
            ...this.$route.query,
            modal: null,
          },
        });
      },
      navigate(id) {
        this.$router.replace({
          name: 'computers-computer-log-record',
          params: { record: id },
          query: this.$route.query,
        });
      },
      onArrowUp(idx) {
        this.focus(idx - this.columnsPerRow);
      },
      onArrowLeft(idx) {
        this.focus(idx - 1);
      },
      onArrowRight(idx) {
        this.focus(idx + 1);
      },
      onArrowDown(idx) {
        this.focus(idx + this.columnsPerRow);
      },
      async focus(index) {
        if (index < 0 || index >= this.screenshots.length) return;

        const { wrapper } = this.$refs;
        if (!wrapper) return;

        const row = Math.floor(index / this.columnsPerRow);
        const rowTop = row * this.rowHeight;
        const rowBottom = rowTop + this.rowHeight;
        const vTop = wrapper.scrollTop;
        const vBottom = vTop + wrapper.clientHeight;

        if (rowTop < vTop) {
          wrapper.scrollTop = rowTop;
        } else if (rowBottom > vBottom) {
          wrapper.scrollTop = rowBottom - wrapper.clientHeight;
        }

        this.scrollTop = wrapper.scrollTop;

        await this.$nextTick();
        await new Promise((resolve) => {
          requestAnimationFrame(() => requestAnimationFrame(resolve));
        });

        const el = wrapper.querySelector(`[data-index="${index}"]`);
        if (el) el.focus();
      },
    },
  };
</script>

<style lang="scss" module>
  .wrapper {
    width: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin !important;
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    border-top: 1px solid $borderColor;
    border-left: 1px solid $borderColor;
  }

  .itemTable {
    display: flex;
    flex-direction: column;
    cursor: pointer;
    text-decoration: none;
    padding: 10px;
    background: $white;
    border-right: 1px solid $borderColor;
    border-bottom: 1px solid $borderColor;
    overflow: hidden;
    box-sizing: border-box;
  }

  .itemTableActive {
    background: $light-blue;
  }

  .title {
    font-size: 12px;
    margin-bottom: 6px;
    flex: 0 0 auto;
  }

  .imageWrap {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
  }

  .image {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
</style>
